import React, { useState, useEffect, useCallback } from "react";
import { unitAPI, lessonAPI, teksAPI } from "../../services/api";

const emptyUnitDraft = () => ({
  unit_number: "",
  title: "",
  rationale: "",
  start_date: "",
  end_date: "",
});

const emptyLessonDraft = () => ({
  lesson_title: "",
  teks_id: "",
  chapter_ref: "",
  essential_questions: "",
});

function formatDate(dateStr) {
  if (!dateStr) return "";
  const raw = String(dateStr).trim().slice(0, 10); // "YYYY-MM-DD"
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return dateStr;
  const d = new Date(raw + "T00:00:00Z");
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function UnitRow({ unit, courseTeks, onRefresh, onRefreshKpis }) {
  const [open, setOpen] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [loadingLessons, setLoadingLessons] = useState(false);
  const [editingUnit, setEditingUnit] = useState(false);
  const [unitDraft, setUnitDraft] = useState(emptyUnitDraft());
  const [saving, setSaving] = useState(false);
  const [deleteUnitConfirm, setDeleteUnitConfirm] = useState(false);

  // Lesson edit state
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [lessonDraft, setLessonDraft] = useState(emptyLessonDraft());
  const [addLessonOpen, setAddLessonOpen] = useState(false);
  const [addLessonDraft, setAddLessonDraft] = useState(emptyLessonDraft());
  const [deleteLessonConfirm, setDeleteLessonConfirm] = useState(null);

  const loadLessons = useCallback(async () => {
    setLoadingLessons(true);
    try {
      const res = await lessonAPI.getAll({ unitId: unit.id });
      setLessons(res.data || []);
    } catch (_) {
      setLessons([]);
    } finally {
      setLoadingLessons(false);
    }
  }, [unit.id]);

  const handleToggle = () => {
    const opening = !open;
    setOpen(opening);
    if (opening && lessons.length === 0) {
      loadLessons();
    }
  };

  // ---- Unit edit ----
  const startEditUnit = () => {
    setUnitDraft({
      unit_number: unit.unit_number != null ? String(unit.unit_number) : "",
      title: unit.title || "",
      rationale: unit.rationale || "",
      start_date: unit.start_date ? unit.start_date.slice(0, 10) : "",
      end_date: unit.end_date ? unit.end_date.slice(0, 10) : "",
    });
    setEditingUnit(true);
  };

  const saveUnit = async () => {
    if (!unitDraft.title.trim()) {
      alert("Unit title is required.");
      return;
    }
    setSaving(true);
    try {
      await unitAPI.update(unit.id, {
        unit_number: unitDraft.unit_number !== "" ? Number(unitDraft.unit_number) : null,
        title: unitDraft.title.trim(),
        rationale: unitDraft.rationale.trim() || null,
        start_date: unitDraft.start_date || null,
        end_date: unitDraft.end_date || null,
      });
      setEditingUnit(false);
      onRefresh();
    } catch (err) {
      alert("Failed to save unit: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const deleteUnit = async () => {
    setSaving(true);
    try {
      await unitAPI.delete(unit.id);
      onRefresh();
      if (onRefreshKpis) onRefreshKpis();
    } catch (err) {
      alert("Failed to delete unit: " + (err.response?.data?.error || err.message));
      setSaving(false);
    }
  };

  // ---- Lesson edit ----
  const startEditLesson = (lesson) => {
    setAddLessonOpen(false);
    setDeleteLessonConfirm(null);
    setEditingLessonId(lesson.id);
    setLessonDraft({
      lesson_title: lesson.lesson_title || "",
      teks_id: lesson.teks_id || lesson.primary_teks_id || "",
      chapter_ref: lesson.chapter_ref || "",
      essential_questions: lesson.essential_questions || "",
    });
  };

  const saveLesson = async () => {
    if (!lessonDraft.lesson_title.trim()) {
      alert("Lesson title is required.");
      return;
    }
    setSaving(true);
    try {
      await lessonAPI.update(editingLessonId, {
        lesson_title: lessonDraft.lesson_title.trim(),
        teks_id: lessonDraft.teks_id || null,
        chapter_ref: lessonDraft.chapter_ref.trim() || null,
        essential_questions: lessonDraft.essential_questions.trim() || null,
      });
      setEditingLessonId(null);
      setLessonDraft(emptyLessonDraft());
      await loadLessons();
    } catch (err) {
      alert("Failed to save lesson: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const addLesson = async () => {
    if (!addLessonDraft.lesson_title.trim()) {
      alert("Lesson title is required.");
      return;
    }
    if (!addLessonDraft.teks_id) {
      alert("A primary TEKS standard is required for each lesson.");
      return;
    }
    setSaving(true);
    try {
      await lessonAPI.create({
        unit_id: unit.id,
        lesson_title: addLessonDraft.lesson_title.trim(),
        teks_id: addLessonDraft.teks_id,
        chapter_ref: addLessonDraft.chapter_ref.trim() || null,
        essential_questions: addLessonDraft.essential_questions.trim() || null,
      });
      setAddLessonOpen(false);
      setAddLessonDraft(emptyLessonDraft());
      await loadLessons();
      if (onRefreshKpis) onRefreshKpis();
    } catch (err) {
      alert("Failed to add lesson: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const deleteLesson = async (lessonId) => {
    setSaving(true);
    try {
      await lessonAPI.delete(lessonId);
      setDeleteLessonConfirm(null);
      await loadLessons();
      if (onRefreshKpis) onRefreshKpis();
    } catch (err) {
      alert("Failed to delete lesson: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="curriculum-unit-accordion">
      {/* Unit header */}
      {editingUnit ? (
        <div className="curriculum-unit-edit-form">
          <div className="curriculum-form-row">
            <label>
              Unit #
              <input
                type="number"
                value={unitDraft.unit_number}
                onChange={(e) =>
                  setUnitDraft({ ...unitDraft, unit_number: e.target.value })
                }
                style={{ width: "70px" }}
              />
            </label>
            <label style={{ flex: 3 }}>
              Title *
              <input
                value={unitDraft.title}
                onChange={(e) =>
                  setUnitDraft({ ...unitDraft, title: e.target.value })
                }
              />
            </label>
            <label>
              Start Date
              <input
                type="date"
                value={unitDraft.start_date}
                onChange={(e) =>
                  setUnitDraft({ ...unitDraft, start_date: e.target.value })
                }
              />
            </label>
            <label>
              End Date
              <input
                type="date"
                value={unitDraft.end_date}
                onChange={(e) =>
                  setUnitDraft({ ...unitDraft, end_date: e.target.value })
                }
              />
            </label>
          </div>
          <div className="curriculum-form-row">
            <label className="curriculum-full-width">
              Rationale
              <textarea
                value={unitDraft.rationale}
                onChange={(e) =>
                  setUnitDraft({ ...unitDraft, rationale: e.target.value })
                }
                rows={2}
                placeholder="Why this unit? What's the big idea?"
              />
            </label>
          </div>
          <div className="curriculum-form-actions">
            <button
              className="primary-btn curriculum-btn-sm"
              onClick={saveUnit}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save Unit"}
            </button>
            <button
              className="secondary-btn curriculum-btn-sm"
              onClick={() => setEditingUnit(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : deleteUnitConfirm ? (
        <div className="curriculum-unit-header" style={{ background: "#fff5f5" }}>
          <span>
            Delete <strong>Unit {unit.unit_number}: {unit.title}</strong> and all
            its lessons?
          </span>
          <button
            className="danger-btn curriculum-btn-sm"
            onClick={deleteUnit}
            disabled={saving}
            style={{ marginLeft: "0.75rem" }}
          >
            {saving ? "Deleting…" : "Yes, Delete"}
          </button>
          <button
            className="secondary-btn curriculum-btn-sm"
            onClick={() => setDeleteUnitConfirm(false)}
            style={{ marginLeft: "0.375rem" }}
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="curriculum-unit-header" onClick={handleToggle}>
          <span className="curriculum-unit-toggle">{open ? "▾" : "▸"}</span>
          <span className="curriculum-unit-number">Unit {unit.unit_number}</span>
          <span className="curriculum-unit-title">{unit.title}</span>
          {(unit.start_date || unit.end_date) && (
            <span className="curriculum-unit-dates">
              {formatDate(unit.start_date)}
              {unit.start_date && unit.end_date && " – "}
              {formatDate(unit.end_date)}
            </span>
          )}
          <span className="curriculum-unit-lesson-count">
            {unit.lesson_count != null ? unit.lesson_count : lessons.length} lessons
          </span>
          <button
            className="secondary-btn curriculum-btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              startEditUnit();
            }}
          >
            Edit
          </button>
          <button
            className="danger-btn curriculum-btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteUnitConfirm(true);
            }}
          >
            Delete
          </button>
        </div>
      )}

      {/* Unit body (lessons) */}
      {open && !editingUnit && (
        <div className="curriculum-unit-body">
          {unit.rationale && (
            <p className="curriculum-unit-rationale">{unit.rationale}</p>
          )}

          {loadingLessons ? (
            <p className="curriculum-loading">Loading lessons…</p>
          ) : (
            <>
              {lessons.length > 0 && (
                <div className="curriculum-lesson-table-wrap">
                  <table className="curriculum-table curriculum-table-sm">
                    <thead>
                      <tr>
                        <th>Lesson Title</th>
                        <th>Primary TEKS</th>
                        <th>Chapter Ref</th>
                        <th>Essential Questions</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lessons.map((lesson) => {
                        if (editingLessonId === lesson.id) {
                          return (
                            <tr key={lesson.id} className="curriculum-editing-row">
                              <td>
                                <input
                                  value={lessonDraft.lesson_title}
                                  onChange={(e) =>
                                    setLessonDraft({
                                      ...lessonDraft,
                                      lesson_title: e.target.value,
                                    })
                                  }
                                />
                              </td>
                              <td>
                                <select
                                  value={lessonDraft.teks_id}
                                  onChange={(e) =>
                                    setLessonDraft({
                                      ...lessonDraft,
                                      teks_id: e.target.value,
                                    })
                                  }
                                >
                                  <option value="">— select TEKS —</option>
                                  {courseTeks.map((t) => (
                                    <option key={t.id} value={t.id}>
                                      {t.teks_code}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                <input
                                  value={lessonDraft.chapter_ref}
                                  onChange={(e) =>
                                    setLessonDraft({
                                      ...lessonDraft,
                                      chapter_ref: e.target.value,
                                    })
                                  }
                                  placeholder="e.g. Ch. 5.2"
                                />
                              </td>
                              <td>
                                <textarea
                                  value={lessonDraft.essential_questions}
                                  onChange={(e) =>
                                    setLessonDraft({
                                      ...lessonDraft,
                                      essential_questions: e.target.value,
                                    })
                                  }
                                  rows={2}
                                />
                              </td>
                              <td className="curriculum-row-actions">
                                <button
                                  className="primary-btn curriculum-btn-sm"
                                  onClick={saveLesson}
                                  disabled={saving}
                                >
                                  {saving ? "…" : "Save"}
                                </button>
                                <button
                                  className="secondary-btn curriculum-btn-sm"
                                  onClick={() => setEditingLessonId(null)}
                                >
                                  Cancel
                                </button>
                              </td>
                            </tr>
                          );
                        }

                        if (deleteLessonConfirm === lesson.id) {
                          return (
                            <tr key={lesson.id} className="curriculum-delete-row">
                              <td colSpan={5}>
                                <span>
                                  Delete lesson{" "}
                                  <strong>{lesson.lesson_title}</strong>?
                                </span>
                                <button
                                  className="danger-btn curriculum-btn-sm"
                                  onClick={() => deleteLesson(lesson.id)}
                                  disabled={saving}
                                  style={{ marginLeft: "0.75rem" }}
                                >
                                  {saving ? "…" : "Yes, Delete"}
                                </button>
                                <button
                                  className="secondary-btn curriculum-btn-sm"
                                  onClick={() => setDeleteLessonConfirm(null)}
                                  style={{ marginLeft: "0.375rem" }}
                                >
                                  Cancel
                                </button>
                              </td>
                            </tr>
                          );
                        }

                        return (
                          <tr key={lesson.id}>
                            <td>{lesson.lesson_title}</td>
                            <td className="curriculum-code-cell">
                              {lesson.teks_code ||
                                lesson.primary_teks_code ||
                                "—"}
                            </td>
                            <td>{lesson.chapter_ref || "—"}</td>
                            <td
                              style={{
                                maxWidth: "220px",
                                fontSize: "0.78rem",
                                color: "#555",
                              }}
                            >
                              {lesson.essential_questions
                                ? lesson.essential_questions.length > 80
                                  ? lesson.essential_questions.slice(0, 80) + "…"
                                  : lesson.essential_questions
                                : "—"}
                            </td>
                            <td className="curriculum-row-actions">
                              <button
                                className="secondary-btn curriculum-btn-sm"
                                onClick={() => startEditLesson(lesson)}
                              >
                                Edit
                              </button>
                              <button
                                className="danger-btn curriculum-btn-sm"
                                onClick={() => {
                                  setEditingLessonId(null);
                                  setDeleteLessonConfirm(lesson.id);
                                }}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {lessons.length === 0 && (
                <p
                  className="curriculum-empty"
                  style={{ padding: "0.5rem 0", textAlign: "left" }}
                >
                  No lessons yet.
                </p>
              )}

              {/* Add lesson form */}
              {addLessonOpen ? (
                <div className="curriculum-add-lesson-form">
                  <h5>Add Lesson to Unit {unit.unit_number}</h5>
                  <div className="curriculum-form-row">
                    <label style={{ flex: 2 }}>
                      Lesson Title *
                      <input
                        value={addLessonDraft.lesson_title}
                        onChange={(e) =>
                          setAddLessonDraft({
                            ...addLessonDraft,
                            lesson_title: e.target.value,
                          })
                        }
                        placeholder="e.g. Introduction to Forces"
                      />
                    </label>
                    <label>
                      Primary TEKS *
                      <select
                        value={addLessonDraft.teks_id}
                        onChange={(e) =>
                          setAddLessonDraft({
                            ...addLessonDraft,
                            teks_id: e.target.value,
                          })
                        }
                      >
                        <option value="">— select TEKS —</option>
                        {courseTeks.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.teks_code}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Chapter Ref
                      <input
                        value={addLessonDraft.chapter_ref}
                        onChange={(e) =>
                          setAddLessonDraft({
                            ...addLessonDraft,
                            chapter_ref: e.target.value,
                          })
                        }
                        placeholder="e.g. Ch. 5.2"
                      />
                    </label>
                  </div>
                  <div className="curriculum-form-row">
                    <label className="curriculum-full-width">
                      Essential Questions
                      <textarea
                        value={addLessonDraft.essential_questions}
                        onChange={(e) =>
                          setAddLessonDraft({
                            ...addLessonDraft,
                            essential_questions: e.target.value,
                          })
                        }
                        rows={2}
                        placeholder="What should students be able to answer after this lesson?"
                      />
                    </label>
                  </div>
                  <div className="curriculum-form-actions">
                    <button
                      className="primary-btn curriculum-btn-sm"
                      onClick={addLesson}
                      disabled={saving}
                    >
                      {saving ? "Saving…" : "Add Lesson"}
                    </button>
                    <button
                      className="secondary-btn curriculum-btn-sm"
                      onClick={() => {
                        setAddLessonOpen(false);
                        setAddLessonDraft(emptyLessonDraft());
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="secondary-btn curriculum-add-lesson-btn"
                  onClick={() => {
                    setEditingLessonId(null);
                    setDeleteLessonConfirm(null);
                    setAddLessonDraft(emptyLessonDraft());
                    setAddLessonOpen(true);
                  }}
                >
                  + Add Lesson
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function UnitsLessonsTab({ courseId, onRefreshKpis }) {
  const [units, setUnits] = useState([]);
  const [courseTeks, setCourseTeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [addUnitOpen, setAddUnitOpen] = useState(false);
  const [addUnitDraft, setAddUnitDraft] = useState(emptyUnitDraft());
  const [saving, setSaving] = useState(false);

  const loadUnits = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    setError("");
    try {
      const [unitsRes, teksRes] = await Promise.all([
        unitAPI.getAll(courseId),
        teksAPI.getAll(courseId),
      ]);
      setUnits(unitsRes.data || []);
      setCourseTeks(teksRes.data || []);
    } catch (err) {
      setError("Failed to load units.");
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    loadUnits();
  }, [loadUnits]);

  const handleAddUnit = async () => {
    if (!addUnitDraft.title.trim()) {
      alert("Unit title is required.");
      return;
    }
    setSaving(true);
    try {
      await unitAPI.create({
        course_id: courseId,
        unit_number:
          addUnitDraft.unit_number !== ""
            ? Number(addUnitDraft.unit_number)
            : units.length + 1,
        title: addUnitDraft.title.trim(),
        rationale: addUnitDraft.rationale.trim() || null,
        start_date: addUnitDraft.start_date || null,
        end_date: addUnitDraft.end_date || null,
      });
      setAddUnitOpen(false);
      setAddUnitDraft(emptyUnitDraft());
      await loadUnits();
      if (onRefreshKpis) onRefreshKpis();
    } catch (err) {
      alert(
        "Failed to create unit: " + (err.response?.data?.error || err.message)
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="curriculum-loading">Loading units…</p>;
  if (error) return <p className="curriculum-error">{error}</p>;

  return (
    <div className="curriculum-units-tab">
      {/* Toolbar */}
      <div className="curriculum-tab-toolbar">
        <button className="primary-btn" onClick={() => setAddUnitOpen(true)}>
          + Add Unit
        </button>
        <span className="curriculum-count">
          {units.length} unit{units.length !== 1 ? "s" : ""}
        </span>
      </div>

      {courseTeks.length === 0 && (
        <div
          style={{
            background: "#fffbeb",
            border: "1px solid #fde68a",
            borderRadius: "6px",
            padding: "0.625rem 1rem",
            marginBottom: "0.75rem",
            fontSize: "0.85rem",
            color: "#92400e",
          }}
        >
          No TEKS standards found for this course. Add TEKS first (in the TEKS tab) before adding lessons — each lesson requires a primary TEKS.
        </div>
      )}

      {/* Add unit form */}
      {addUnitOpen && (
        <div className="curriculum-add-unit-section">
          <h4>Add Unit</h4>
          <div className="curriculum-form-row">
            <label>
              Unit #
              <input
                type="number"
                value={addUnitDraft.unit_number}
                onChange={(e) =>
                  setAddUnitDraft({
                    ...addUnitDraft,
                    unit_number: e.target.value,
                  })
                }
                placeholder={String(units.length + 1)}
                style={{ width: "70px" }}
              />
            </label>
            <label style={{ flex: 3 }}>
              Title *
              <input
                value={addUnitDraft.title}
                onChange={(e) =>
                  setAddUnitDraft({ ...addUnitDraft, title: e.target.value })
                }
                placeholder="Unit title"
              />
            </label>
            <label>
              Start Date
              <input
                type="date"
                value={addUnitDraft.start_date}
                onChange={(e) =>
                  setAddUnitDraft({
                    ...addUnitDraft,
                    start_date: e.target.value,
                  })
                }
              />
            </label>
            <label>
              End Date
              <input
                type="date"
                value={addUnitDraft.end_date}
                onChange={(e) =>
                  setAddUnitDraft({
                    ...addUnitDraft,
                    end_date: e.target.value,
                  })
                }
              />
            </label>
          </div>
          <div className="curriculum-form-row">
            <label className="curriculum-full-width">
              Rationale
              <textarea
                value={addUnitDraft.rationale}
                onChange={(e) =>
                  setAddUnitDraft({
                    ...addUnitDraft,
                    rationale: e.target.value,
                  })
                }
                rows={2}
                placeholder="Why this unit? What's the big idea?"
              />
            </label>
          </div>
          <div className="curriculum-form-actions">
            <button
              className="primary-btn"
              onClick={handleAddUnit}
              disabled={saving}
            >
              {saving ? "Creating…" : "Create Unit"}
            </button>
            <button
              className="secondary-btn"
              onClick={() => {
                setAddUnitOpen(false);
                setAddUnitDraft(emptyUnitDraft());
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Units list */}
      {units.length === 0 && !addUnitOpen ? (
        <p className="curriculum-empty">
          No units yet. Click <strong>+ Add Unit</strong> to create the first one.
        </p>
      ) : (
        <div className="curriculum-units-list">
          {units.map((unit) => (
            <UnitRow
              key={unit.id}
              unit={unit}
              courseTeks={courseTeks}
              onRefresh={loadUnits}
              onRefreshKpis={onRefreshKpis}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default UnitsLessonsTab;
